import React, { useMemo, useState } from 'react';
import {
  Header,
  Content,
  Page,
  EmptyState,
  Progress,
  ResponseErrorPanel,
  LinkButton,
} from '@backstage/core-components';
import {
  HomePageToolkit,
  HomePageStarredEntities,
  HomePageRecentlyVisited,
  HeaderWorldClock,
  ComponentAccordion,
  WelcomeTitle,
  CustomHomepageGrid,
} from '@backstage/plugin-home';
import type { LayoutConfiguration } from '@backstage/plugin-home';
import {
  ServiceHealthHomeCard,
  OpenPullRequestsHomeCard,
  ServicesHomeCard,
  PendingWorkHomeCard,
} from './cards';
import { AnnouncementsCard } from '@backstage-community/plugin-announcements';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  AnalyticsContext,
  useAnalytics,
} from '@backstage/core-plugin-api';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { HomeScopeProvider, useHomeScope } from './HomeScopeContext';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
// Quick-link icons
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { ReadOnlyAccessNotice } from './ReadOnlyAccessNotice';
import { M42PluginBanner } from './M42PluginBanner';
import { SCOPE_EDIT_BAND_MEDIA } from '../../theme/customTheme';
import { TeamAvatar } from './TeamAvatar';
import { useViewAs } from '../viewAs/ViewAsContext';

const customLanguages = ['English'];

// Compact world clock lives in the Header bar (top-right), not as a hero row.
const customClockConfigs = [
  { label: 'PT', timeZone: 'America/Los_Angeles' },
  { label: 'MT', timeZone: 'America/Denver' },
  { label: 'CT', timeZone: 'America/Chicago' },
  { label: 'ET', timeZone: 'America/New_York' },
  { label: 'UTC', timeZone: 'UTC' },
];





// Multi-select scope picker as a single trigger button + popover (replaces the
// earlier chip row). Single control regardless of team count (scales cleanly
// to 50+ teams), one consistent visual element, popover gives room for search
// + avatar-rich rows.
const useScopeStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    rowGap: theme.spacing(0.75),
  },
  scopeLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginRight: theme.spacing(0.5),
  },
  trigger: {
    textTransform: 'none',
    fontWeight: 500,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(0.5, 1.25),
    gap: theme.spacing(0.75),
    minHeight: 32,
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.text.secondary,
    },
  },
  triggerOpen: {
    borderColor: theme.palette.primary.main,
  },
  triggerLabel: {
    fontWeight: 500,
    // MUI Button wraps children in .MuiButton-label without gap; pad the text
    // away from the leading avatar(s) manually.
    marginLeft: theme.spacing(0.75),
  },
  // Stacked avatars when 2+ teams selected (Linear-style overlap).
  stackedAvatars: {
    display: 'inline-flex',
    alignItems: 'center',
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(-0.75),
    },
    '& > *': {
      // White ring so overlapping avatars read as distinct entities.
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
  },
  popoverPaper: {
    width: 320,
    maxHeight: 480,
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  popoverHeader: {
    padding: theme.spacing(1, 1.5, 0.5),
  },
  popoverSection: {
    overflowY: 'auto',
    flex: 1,
  },
  popoverRow: {
    padding: theme.spacing(0.75, 1.5),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: theme.spacing(1, 1.5, 0.5),
  },
  popoverFooter: {
    padding: theme.spacing(0.5, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  clearButton: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  // Secondary line under entity names (type · lifecycle).
  secondaryMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  // ScopeRow + CustomHomepageGrid share one band: the grid renders its own
  // ContentHeader (the Edit / Save / Cancel controls) as a full-width row,
  // which left the scope row's empty right half followed by a second
  // near-empty row. This class only provides the positioning context; the
  // actual header pin lives in the theme (customTheme.ts,
  // BackstageContentHeader override scoped to `.m42-scope-edit-band`),
  // because production JSS minifies Backstage class names, so any
  // `[class*="BackstageContentHeader-..."]` selector here would work in dev
  // and silently no-op in prod. The shared media literal (not a breakpoint
  // token) keeps this in lockstep with the pin — the theme override side
  // resolves `md` to a different value than makeStyles does.
  scopeEditBand: {
    [SCOPE_EDIT_BAND_MEDIA]: {
      position: 'relative',
    },
  },
  // Right edge of the scope row: holds the View As control (the services-in-
  // scope counter that used to live here never earned its glance).
  rowEnd: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    // Match the row's own item gap so "View as" sits off its trigger exactly
    // like "Scope" does (label margin + gap = ~12px on both sides).
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: '100%',
    },
  },
}));

// One unified set of quick links for everyone (no team gating). Accent icons here
// read as launch affordances; the global semantic-icon cleanup is a separate task.
// Only links the sidebar does NOT already provide. Catalog / APIs / Docs were
// duplicates of sidebar nav items; duplicating navigation in a card teaches
// users to ignore the card.
const quickLinks = [
  { url: '/create?filters[kind]=template&filters[user]=all', label: 'Launch a template', icon: <CreateComponentIcon /> },
  { url: '/create/templates/default/spring-promotion', label: 'Promotion workflow', icon: <UnarchiveIcon /> },
];

const ExpandedAccordion = (props: any) => <ComponentAccordion expanded {...props} />;

// Default dashboard layout (12-col grid, rowHeight 60). Reproduces today's
// arrangement: PRs span the main column, Services + Service health sit
// side-by-side beneath, the side column stacks Pending work / Announcements /
// Starred / Quick links. Users can drag/resize/show-hide from here; the grid
// persists their changes per user (CustomHomepageGrid + storageApi).
const DEFAULT_LAYOUT: LayoutConfiguration[] = [
  // Main column (x0, 8 wide): PRs on top, then Services + Service health
  // side-by-side, then Recently visited.
  { component: <OpenPullRequestsHomeCard />, x: 0, y: 0, width: 8, height: 8, movable: true, resizable: true, deletable: true },
  { component: <ServicesHomeCard />, x: 0, y: 8, width: 4, height: 5, movable: true, resizable: true, deletable: true },
  { component: <ServiceHealthHomeCard />, x: 4, y: 8, width: 4, height: 5, movable: true, resizable: true, deletable: true },
  { component: <HomePageRecentlyVisited />, x: 0, y: 13, width: 8, height: 4, movable: true, resizable: true, deletable: true },
  // Side column (x8, 4 wide): Pending work, Announcements, Starred, Quick links.
  { component: <PendingWorkHomeCard />, x: 8, y: 0, width: 4, height: 5, movable: true, resizable: true, deletable: true },
  { component: <AnnouncementsCard max={2} />, x: 8, y: 5, width: 4, height: 4, movable: true, resizable: true, deletable: true },
  { component: <HomePageStarredEntities />, x: 8, y: 9, width: 4, height: 4, movable: true, resizable: true, deletable: true },
  { component: <HomePageToolkit tools={quickLinks} title="Quick links" Renderer={ExpandedAccordion} />, x: 8, y: 13, width: 4, height: 4, movable: true, resizable: true, deletable: true },
];

// "Services you own": ownership-driven list + scope filter. Empty (guest / no
// ownership) -> teaching activation state. Loading/error preserved.
export const OwnedEntitiesContent = ({
  entities,
  totalUnscoped,
  loading,
  error,
  viewAllHref,
}: {
  entities: Entity[];
  totalUnscoped: number;
  loading: boolean;
  error?: Error;
  // Catalog URL replaying the EXACT refs/facets this list was computed from
  // (filters[user]=owned is claims-based and undercounts; see effectiveScopeRefs).
  viewAllHref: string;
}) => {
  const classes = useScopeStyles();
  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;
  if (totalUnscoped === 0) {
    return (
      <EmptyState
        missing="content"
        title="You don't own any services yet"
        description="Components owned by your team appear here for one-click access. Browse the catalog to find a service, or launch a template to create one."
        action={
          <LinkButton to="/catalog" color="primary" variant="contained">
            Browse the catalog
          </LinkButton>
        }
      />
    );
  }
  if (entities.length === 0) {
    return (
      <Typography color="textSecondary">
        No services in this scope. Switch to{' '}
        <strong>My work</strong> to see everything.
      </Typography>
    );
  }
  // Capped at 5 (was 8) to make room for Announcements above.
  const shown = entities.slice(0, 5);
  return (
    <>
      <List dense>
        {shown.map(entity => (
          <ListItem key={stringifyEntityRef(entity)} disableGutters>
            <ListItemText
              primary={<EntityRefLink entityRef={entity} />}
              secondary={
                <Typography component="span" className={classes.secondaryMeta}>
                  {[entity.spec?.type, entity.spec?.lifecycle].filter(Boolean).join(' · ')}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      {entities.length > shown.length && (
        <LinkButton to={viewAllHref} color="primary" variant="text">
          View all {entities.length} services
        </LinkButton>
      )}
    </>
  );
};

// --- Metrics ---
// Catalog-info-missing: count of scoped entities tagged auto-discovered by the
// githubAutoDiscovery provider (a real signal of "needs a real catalog-info.yaml").
// Open-PR count is GATED by the `features.homePrMetric` feature flag (config
// default false). Production wiring (proxy / GitHub auth token) is out of scope
// for this PR; the placeholder hook returns null until the flag flips on AND a
// concrete data source is wired. This keeps the UI shape ready without shipping
// a half-functional chip.
// Scope picker. Display scoping only -- the permission policy still owns access.
// Inline chips for "My work" + up to MAX_INLINE_TEAMS teams (alphabetical), with
// the rest behind a "+N more" overflow Menu. The currently-selected team is
// ALWAYS surfaced inline even if alphabetical order would push it past the
// threshold (avoids the "where did my selection go?" UX trap when a manager has
// 10+ teams). Renders nothing when the user belongs to <=1 team.

export const ScopeRow = ({
  teamRefs,
  scopes,
  setScopes,
  teamsByRef,
  currentUser,
  subTeams,
  setSubTeams,
  hasSubTeams,
  viewingAs,
}: {
  teamRefs: string[];
  scopes: string[];
  setScopes: (next: string[]) => void;
  teamsByRef: Record<string, Entity>;
  currentUser?: Entity;
  subTeams: boolean;
  setSubTeams: (next: boolean) => void;
  hasSubTeams: boolean;
  // The "View as" lens now lives in the sidebar (app-wide). We still keep this
  // row mounted for single-team users while the lens is active, so the viewed
  // user's scope controls stay reachable.
  viewingAs?: boolean;
}) => {
  const classes = useScopeStyles();
  const analytics = useAnalytics();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  const teamName = (ref: string) => {
    try { return parseEntityRef(ref).name; } catch { return ref; }
  };
  const pictureOf = (e: Entity | undefined) =>
    (e?.spec?.profile as { picture?: string } | undefined)?.picture;

  // Single-team / no-team users skip the picker -- the union is the only
  // sensible view. Never hide while a viewAsSlot is present: the viewed user
  // may have one team, but the admin still needs the lens controls.
  const hideIf = teamRefs.length <= 1 && !hasSubTeams && !viewingAs;

  const sortedTeams = useMemo(
    () => [...teamRefs].sort((a, b) => teamName(a).localeCompare(teamName(b))),
    [teamRefs],
  );
  // Search input renders only when there are enough teams to make scanning
  // a real cost; below the threshold the popover just scrolls.
  const SEARCH_THRESHOLD = 8;
  const showSearch = sortedTeams.length > SEARCH_THRESHOLD;
  const filteredTeams = useMemo(() => {
    if (!query) return sortedTeams;
    const q = query.toLowerCase();
    return sortedTeams.filter(r => teamName(r).toLowerCase().includes(q));
  }, [sortedTeams, query]);

  const isAllSelected = scopes.length === 0;
  const togglePickTeam = (ref: string) => {
    const adding = !scopes.includes(ref);
    setScopes(adding ? [...scopes, ref] : scopes.filter(s => s !== ref));
    // Whether the team scope filter gets used at all, and how wide. surface
    // attribute is supplied by the AnalyticsContext around each page.
    analytics.captureEvent(adding ? 'select' : 'deselect', 'scope-team');
  };
  const clearTeams = () => {
    setScopes([]);
    analytics.captureEvent('clear', 'scope-team');
  };
  const close = () => { setAnchor(null); setQuery(''); };

  if (hideIf) return null;

  const userPic = pictureOf(currentUser);
  const userSlug = currentUser?.metadata.name ?? 'me';

  // Trigger button content varies by selection count.
  const renderTriggerVisual = () => {
    if (scopes.length === 0) {
      return (
        <>
          <TeamAvatar slug={userSlug} pictureUrl={userPic} size={20} />
          <span className={classes.triggerLabel}>My work</span>
        </>
      );
    }
    if (scopes.length === 1) {
      const ref = scopes[0];
      const team = teamsByRef[ref];
      return (
        <>
          <TeamAvatar slug={teamName(ref)} pictureUrl={pictureOf(team)} size={20} />
          <span className={classes.triggerLabel}>{teamName(ref)}</span>
        </>
      );
    }
    const previewRefs = scopes.slice(0, 3);
    return (
      <>
        <span className={classes.stackedAvatars}>
          {previewRefs.map(ref => (
            <TeamAvatar
              key={ref}
              slug={teamName(ref)}
              pictureUrl={pictureOf(teamsByRef[ref])}
              size={20}
            />
          ))}
        </span>
        <span className={classes.triggerLabel}>{scopes.length} teams</span>
      </>
    );
  };

  return (
    <Box className={classes.row}>
      <Typography className={classes.scopeLabel} component="span" aria-hidden>
        Scope
      </Typography>
      <Button
        className={`${classes.trigger}${anchor ? ` ${classes.triggerOpen}` : ''}`}
        onClick={e => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-label={
          scopes.length === 0
            ? 'Filter by team (currently My work)'
            : scopes.length === 1
            ? `Filter by team (currently ${teamName(scopes[0])})`
            : `Filter by team (currently ${scopes.length} teams selected)`
        }
        endIcon={<ExpandMoreIcon fontSize="small" />}
      >
        {renderTriggerVisual()}
      </Button>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        {showSearch && (
          <Box className={classes.popoverHeader}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search teams"
              value={query}
              onChange={e => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ 'aria-label': 'Search teams' }}
            />
          </Box>
        )}
        <Box className={classes.popoverSection}>
          <MenuItem
            className={classes.popoverRow}
            selected={isAllSelected}
            onClick={clearTeams}
          >
            <ListItemIcon style={{ minWidth: 32 }}>
              <TeamAvatar slug={userSlug} pictureUrl={userPic} size={22} />
            </ListItemIcon>
            <ListItemText
              primary="All my work"
              secondary={isAllSelected ? 'No team filter applied' : undefined}
            />
            <Checkbox
              edge="end"
              size="small"
              color="primary"
              checked={isAllSelected}
              tabIndex={-1}
              disableRipple
            />
          </MenuItem>
          {filteredTeams.length > 0 && (
            <>
              <Divider />
              <Typography className={classes.sectionLabel} component="div">
                My teams
              </Typography>
              {filteredTeams.map(ref => {
                const team = teamsByRef[ref];
                const slug = teamName(ref);
                const sel = scopes.includes(ref);
                return (
                  <MenuItem
                    key={ref}
                    className={classes.popoverRow}
                    selected={sel}
                    onClick={() => togglePickTeam(ref)}
                  >
                    <ListItemIcon style={{ minWidth: 32 }}>
                      <TeamAvatar slug={slug} pictureUrl={pictureOf(team)} size={22} />
                    </ListItemIcon>
                    <ListItemText primary={slug} />
                    <Checkbox
                      edge="end"
                      size="small"
                      color="primary"
                      checked={sel}
                      tabIndex={-1}
                      disableRipple
                    />
                  </MenuItem>
                );
              })}
            </>
          )}
          {filteredTeams.length === 0 && query && (
            <Box className={classes.popoverRow}>
              <Typography variant="body2" color="textSecondary">
                No teams match "{query}".
              </Typography>
            </Box>
          )}
        </Box>
        {hasSubTeams && (
          <Box className={classes.popoverFooter}>
            {/* One toggle redefines the scope for EVERY Home card (Services /
                Pending work / Open PRs / the in-scope count) -- per-card
                toggles would let the cards disagree about what "scope" means. */}
            <MenuItem
              className={classes.popoverRow}
              onClick={() => {
                const next = !subTeams;
                setSubTeams(next);
                analytics.captureEvent('toggle', `scope-subteams:${next ? 'on' : 'off'}`);
              }}
            >
              <ListItemText
                primary="Include sub-team ownership"
                secondary="Also count what descendant teams own"
              />
              <Checkbox
                edge="end"
                size="small"
                color="primary"
                checked={subTeams}
                tabIndex={-1}
                disableRipple
              />
            </MenuItem>
          </Box>
        )}
        {scopes.length > 0 && (
          <Box className={classes.popoverFooter}>
            <Button
              size="small"
              fullWidth
              className={classes.clearButton}
              onClick={clearTeams}
            >
              Clear team filters
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

const HomePageContent = () => {
  // Only the fixed header (ScopeRow + ReadOnlyAccessNotice gate) reads scope
  // here now; the cards pull what they need from useHomeScope() themselves.
  const {
    loading,
    hasValue,
    teamRefs,
    teamsByRef,
    currentUser,
    scopes,
    setScopes,
    subTeams,
    setSubTeams,
    hasSubTeams,
  } = useHomeScope();
  const { viewingAs } = useViewAs();
  const classes = useScopeStyles();

  return (
    // surface attribute is merged into every analytics event captured from
    // within Home (mode switches, scope changes, PR deep-link clicks), so the
    // same shared controls (ScopeRow / ScopedPullRequestsCard) are
    // distinguishable from their use on the /pull-requests page.
    <AnalyticsContext attributes={{ surface: 'home' }}>
    <SearchContextProvider>
      <Page themeId="homePage">
        {/* Greeting on the banner (an empty title fell back to a literal
            "Home" label, which read as redundant chrome). pageTitleOverride
            still sets the browser tab; the search pill overlays the banner. */}
        <Header title={<WelcomeTitle language={customLanguages} />} pageTitleOverride="Home">
          <HeaderWorldClock clockConfigs={customClockConfigs} />
        </Header>
        <Content>
          {/* Fixed above the grid (global controls / alerts, not draggable
              content): the Claude plugin banner, the read-only notice, and
              the team-scope selector. The notice is gated so it stays hidden
              under the View-as lens and until ownership resolves; the banner
              is identity-independent and only gated by its own dismissal. */}
          <M42PluginBanner />
          {!viewingAs && !loading && hasValue && teamRefs.length === 0 && (
            <Box mb={2}>
              <ReadOnlyAccessNotice />
            </Box>
          )}
          {/* One band: the grid's Edit controls are pinned to the scope
              row's right edge on md+. The literal marker class is the
              anchor for the BackstageContentHeader theme override in
              customTheme.ts — keep the two in sync. */}
          <Box className={`${classes.scopeEditBand} m42-scope-edit-band`}>
            <ScopeRow
              teamRefs={teamRefs}
              scopes={scopes}
              setScopes={setScopes}
              teamsByRef={teamsByRef}
              currentUser={currentUser}
              subTeams={subTeams}
              setSubTeams={setSubTeams}
              hasSubTeams={hasSubTeams}
              viewingAs={viewingAs}
            />

            {/* Customizable dashboard (PLAT-384): users drag / resize /
                show-hide via the grid's built-in "Edit" mode; the layout
                persists per user. DEFAULT_LAYOUT reproduces the prior
                static arrangement. */}
            <CustomHomepageGrid config={DEFAULT_LAYOUT}>
              <OpenPullRequestsHomeCard />
              <PendingWorkHomeCard />
              <AnnouncementsCard max={2} />
              <ServicesHomeCard />
              <ServiceHealthHomeCard />
              <HomePageStarredEntities />
              <HomePageRecentlyVisited />
              <HomePageToolkit
                tools={quickLinks}
                title="Quick links"
                Renderer={ExpandedAccordion}
              />
            </CustomHomepageGrid>
          </Box>
        </Content>
      </Page>
    </SearchContextProvider>
    </AnalyticsContext>
  );
};

export const HomePage = () => (
  <HomeScopeProvider>
    <HomePageContent />
  </HomeScopeProvider>
);
